import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { AWESOME_GIF, ROUNDS } from '../../data/brainTeasers';
import { addReportToServer } from '../../functions/sendReport';
import useGameTimer from '../../hooks/useGameTimer';
import { apiPost } from '../../utils/apiClient';
import './RoundPage.css';

/** PHP game.php adjusts .img2 bottom per puzzle index after load. */
const ROUND1_PUZZLE_BOTTOM = ['0%', '15%', '30%', '25%'];

function platformExitUrl(user) {
  return (
    user?.backButtonRedirect ||
    process.env.REACT_APP_BASE_URL ||
    (typeof window !== 'undefined' ? window.location.origin : '/') ||
    '/'
  );
}

export default function RoundPage() {
  const { roundId } = useParams();
  const roundNum = Number(roundId);
  const round = ROUNDS[roundNum];
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth.user);
  const isRound1 = roundNum === 1;

  const [pos, setPos] = useState(0);
  /** true = button is "Check Answer"; false = button is "Next" (PHP checkanswer) */
  const [checkAnswer, setCheckAnswer] = useState(true);
  const [showAnswerModal, setShowAnswerModal] = useState(false);
  const [answerGif, setAnswerGif] = useState('');
  const [saving, setSaving] = useState(false);
  const [showAwesome, setShowAwesome] = useState(false);
  const [statusChecked, setStatusChecked] = useState(!round?.checkCompleted);

  const { timeString } = useGameTimer(Boolean(round?.resetTimer));

  useEffect(() => {
    setPos(0);
    setCheckAnswer(true);
    setShowAnswerModal(false);
    setAnswerGif('');
    setShowAwesome(false);
    setSaving(false);
  }, [roundNum]);

  /* Let full-bleed round BG show through body/shell (no cream strips) */
  useEffect(() => {
    document.body.classList.add('bt-round-active');
    return () => document.body.classList.remove('bt-round-active');
  }, []);

  useEffect(() => {
    if (!round?.checkCompleted) {
      setStatusChecked(true);
      return undefined;
    }
    if (!user?.userId) return undefined;

    setStatusChecked(false);
    let cancelled = false;
    (async () => {
      try {
        const res = await apiPost('/game/status', { userId: user.userId });
        if (cancelled) return;
        if (res.completed) {
          window.location.href = platformExitUrl(user);
          return;
        }
        setStatusChecked(true);
      } catch (err) {
        console.error('game status failed', err);
        if (!cancelled) setStatusChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [roundNum, round?.checkCompleted, user]);

  const finishRound = useCallback(async () => {
    if (roundNum === 1 && user?.sessionId === 'demobypass') {
      Swal.fire({
        title: 'Subscribe to any PLAN to play with your peers.',
        text: '',
        customClass: { popup: 'hf-swal-popup', confirmButton: 'hf-swal-close' },
        buttonsStyling: false,
        timer: 1000,
        showConfirmButton: false,
      });
      setTimeout(() => {
        const base = process.env.REACT_APP_BASE_URL || '';
        window.location.href = `${base}/plans`;
      }, 1000);
      return;
    }

    if (round?.isFinal) {
      if (saving) return;
      setSaving(true);
      try {
        const res = await apiPost('/game/complete', {
          userId: user.userId,
          name: user.firstName || user.name || '',
          time: timeString,
        });
        if (res.ok) {
          // ExtraMile report: this game has no points; platform expects time 00:00:00
          await addReportToServer(user, {
            points: 0,
            reportTime: '00:00:00',
            answers: ['brain_teasers', 'completed'],
          });
          setShowAwesome(true);
          setTimeout(() => navigate('/thank-you'), 3000);
        } else {
          setSaving(false);
        }
      } catch (err) {
        console.error('complete failed', err);
        setSaving(false);
        Swal.fire({
          title: 'Could not save',
          text: err.message || 'Please try again',
          icon: 'error',
          customClass: { popup: 'hf-swal-popup', confirmButton: 'hf-swal-close' },
          buttonsStyling: false,
        });
      }
      return;
    }

    navigate(`/rounds/${round.nextRound}`);
  }, [round, roundNum, user, saving, timeString, navigate]);

  const loadPuzzleOrFinish = useCallback(
    (nextPos) => {
      if (!round) return;
      if (nextPos >= round.puzzles.length) {
        finishRound();
        return;
      }
      setPos(nextPos);
      setCheckAnswer(true);
    },
    [round, finishRound]
  );

  const onPrimaryClick = () => {
    if (!round || showAnswerModal || saving || showAwesome) return;

    if (checkAnswer) {
      const puzzle = round.puzzles[pos];
      if (!puzzle) return;
      setAnswerGif(puzzle.answerGif);
      setShowAnswerModal(true);
      setTimeout(() => {
        setShowAnswerModal(false);
        setPos((p) => p + 1);
        setCheckAnswer(false);
      }, 2000);
      return;
    }

    loadPuzzleOrFinish(pos);
  };

  if (!round) {
    return (
      <div className="bt-round-page">
        <p>Unknown round.</p>
      </div>
    );
  }

  if (!statusChecked) {
    return (
      <div className="bt-round-page bt-round-loading">
        <div className="bt-spinner" />
      </div>
    );
  }

  // After Check Answer, pos is already advanced but the previous puzzle stays visible until Next.
  const shownIndex = checkAnswer ? pos : Math.max(0, pos - 1);
  const shownPuzzle = round.puzzles[shownIndex] || round.puzzles[0];
  const numberLabel = checkAnswer ? pos + 1 : pos;
  const buttonLabel = checkAnswer ? 'Check Answer' : 'Next';
  const btnDisabled = showAnswerModal || saving || showAwesome;
  const puzzleBottom = isRound1
    ? ROUND1_PUZZLE_BOTTOM[shownIndex] || '0%'
    : undefined;

  const actionButton = (extraClass = '') => (
    <button
      type="button"
      className={`bt-next-btn ${extraClass}`.trim()}
      style={{ backgroundColor: round.buttonBg, color: round.buttonColor }}
      onClick={onPrimaryClick}
      disabled={btnDisabled}
    >
      {buttonLabel}
    </button>
  );

  const puzzleFrameStyle =
    round.frameBg && round.frameBg !== 'transparent'
      ? { backgroundColor: round.frameBg }
      : undefined;

  const puzzleImage = (
    <img src={shownPuzzle.image} alt={`Puzzle ${numberLabel}`} className="bt-puzzle-img" />
  );

  return (
    <div
      key={roundNum}
      className={[
        'bt-round-page',
        round.bgType === 'image' ? 'bt-round-image-bg' : '',
        isRound1 ? 'bt-round-1' : 'bt-round-centered',
      ]
        .filter(Boolean)
        .join(' ')}
      style={
        round.bgType === 'image' ? { '--bt-round-bg': `url(${round.bgImage})` } : undefined
      }
    >
      {/* Full-viewport layer so BG covers under header + bottom (PHP body.index-back) */}
      {round.bgType === 'image' ? (
        <div className="bt-fullbleed-bg" aria-hidden />
      ) : null}
      {round.bgType === 'video' && round.video ? (
        <video
          key={roundNum}
          className="bt-fullscreen-video"
          src={round.video}
          playsInline
          autoPlay
          muted
          loop
        />
      ) : null}

      <div className="bt-round-header">
        <h3
          className="bt-round-title"
          style={{ backgroundColor: round.titleBg, color: round.titleColor }}
        >
          {round.title}
        </h3>
        <div
          className="bt-round-number"
          style={{ backgroundColor: round.numberBg, color: round.numberColor }}
        >
          {numberLabel}
        </div>
      </div>

      {isRound1 ? (
        <>
          {/* Mobile: stacked layout (PHP .mob1) */}
          <div className="bt-r1-mobile">
            <div className="bt-values-container" style={puzzleFrameStyle}>
              {puzzleImage}
            </div>
            {actionButton()}
            {round.blockImage ? (
              <img src={round.blockImage} alt="" className="bt-block-image-mobile" />
            ) : null}
          </div>

          {/* Desktop: absolute 3-zone layout (PHP .img1 / .img2 / .img3) */}
          <div className="bt-r1-desktop">
            {round.blockImage ? (
              <img src={round.blockImage} alt="" className="bt-r1-img1" />
            ) : null}
            <div className="bt-r1-img2" style={{ bottom: puzzleBottom }}>
              <div className="bt-values-container" style={puzzleFrameStyle}>
                {puzzleImage}
              </div>
            </div>
            <div className="bt-r1-img3">{actionButton()}</div>
          </div>
        </>
      ) : (
        <div className="bt-round-inner">
          {shownPuzzle.question ? (
            <div className="bt-question-title">{shownPuzzle.question}</div>
          ) : null}

          <div className="bt-values-container bt-values-framed" style={puzzleFrameStyle}>
            {puzzleImage}
          </div>
          {actionButton()}
        </div>
      )}

      {showAnswerModal ? (
        <div className="bt-answer-modal" role="dialog" aria-modal="true">
          <div className="bt-answer-modal-body">
            <img src={answerGif} alt="Answer" className="bt-answer-gif" />
          </div>
        </div>
      ) : null}

      {showAwesome ? (
        <div className="bt-answer-modal" role="dialog" aria-modal="true">
          <div className="bt-answer-modal-body">
            <img src={AWESOME_GIF} alt="Awesome" className="bt-awesome-gif" />
          </div>
        </div>
      ) : null}

      <input type="hidden" value={timeString} readOnly aria-hidden />
    </div>
  );
}
